import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../api/client";

export interface LinkedCharacter {
  character_id: number;
  name: string;
  corporation_id: number;
  corporation_name: string | null;
  alliance_id: number | null;
  alliance_name: string | null;
  esi_token_ok: boolean;
}

export interface AuthUser {
  account: { id: string };
  character_id: number;
  name: string;
  corporation_id: number;
  corporation_name: string | null;
  alliance_id: number | null;
  alliance_name: string | null;
  current_system: { id: string; name: string } | null;
  active_character_id: number;
  characters: LinkedCharacter[];
  is_server_owner: boolean;
  needs_owner_setup: boolean;
}

interface MeFetched {
  user: AuthUser | null;
  pendingOwnerClaim: boolean;
  accessRevoked: boolean;
}

type MeResponse =
  | (AuthUser & { pending_owner_claim: false })
  | { pending_owner_claim: true };

async function fetchMe(): Promise<MeFetched> {
  try {
    const me = await api<MeResponse>("/auth/me");
    if (me.pending_owner_claim === true) {
      return { user: null, pendingOwnerClaim: true, accessRevoked: false };
    }
    const { pending_owner_claim: _unused, ...user } = me;
    return {
      user: user as AuthUser,
      pendingOwnerClaim: false,
      accessRevoked: false,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      return {
        user: null,
        pendingOwnerClaim: false,
        accessRevoked: err.code === "access_revoked",
      };
    }
    throw err;
  }
}

export function useAuth() {
  return useQuery({
    queryKey: ["auth", "me"],
    queryFn: fetchMe,
    refetchOnWindowFocus: true,
    retry: false,
    select: (d) => d.user,
  });
}

export function usePendingOwnerClaim() {
  return useQuery({
    queryKey: ["auth", "me"],
    queryFn: fetchMe,
    refetchOnWindowFocus: true,
    retry: false,
    select: (d) => d.pendingOwnerClaim,
  });
}

export function useAccessRevoked() {
  return useQuery({
    queryKey: ["auth", "me"],
    queryFn: fetchMe,
    refetchOnWindowFocus: true,
    retry: false,
    select: (d) => d.accessRevoked,
  });
}
